import {
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent,
} from 'react'
import { cn } from '@/lib/utils/helpers'
import { OTP_LENGTH } from '../constants/otp'
import type { OtpCodeInputProps } from '../types'

function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, OTP_LENGTH)
}

function isDigitKey(key: string): boolean {
  return key.length === 1 && key >= '0' && key <= '9'
}

/**
 * Six single-digit boxes with auto-advance, backspace navigation, and paste.
 * Digits are applied on keydown so a caret after an existing digit still works
 * (maxLength would otherwise block the new keystroke).
 */
export function OtpCodeInput({
  value,
  onChange,
  disabled = false,
  invalid = false,
  autoFocus = false,
  'aria-label': ariaLabel = 'One-time passcode',
  id = 'otp-code',
}: OtpCodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] ?? '')
  // Keep latest digits for key handlers without stale closures mid-key-repeat
  const digitsRef = useRef(digits)
  digitsRef.current = digits

  const focusIndex = useCallback((index: number) => {
    const el = inputsRef.current[Math.max(0, Math.min(index, OTP_LENGTH - 1))]
    if (!el) return
    el.focus()
    // Select after focus so the next key replaces, even if the caret was at end
    requestAnimationFrame(() => {
      el.select()
    })
  }, [])

  useEffect(() => {
    if (!autoFocus || disabled) return
    const t = window.setTimeout(() => focusIndex(0), 50)
    return () => window.clearTimeout(t)
  }, [autoFocus, disabled, focusIndex])

  const commit = useCallback(
    (nextDigits: string[]) => {
      onChange(nextDigits.join('').slice(0, OTP_LENGTH))
    },
    [onChange],
  )

  const writeDigitAt = useCallback(
    (index: number, digit: string, advance: boolean) => {
      const next = [...digitsRef.current]
      next[index] = digit
      commit(next)
      if (advance && index < OTP_LENGTH - 1) {
        focusIndex(index + 1)
      } else {
        // Reselect so consecutive typing still replaces this cell
        focusIndex(index)
      }
    },
    [commit, focusIndex],
  )

  const applyPaste = useCallback(
    (raw: string, startIndex: number) => {
      const pasted = digitsOnly(raw)
      if (!pasted) return

      if (pasted.length >= OTP_LENGTH) {
        commit(pasted.split(''))
        focusIndex(OTP_LENGTH - 1)
        return
      }

      const next = [...digitsRef.current]
      let writeAt = startIndex
      for (const ch of pasted) {
        if (writeAt >= OTP_LENGTH) break
        next[writeAt] = ch
        writeAt += 1
      }
      commit(next)
      focusIndex(Math.min(writeAt, OTP_LENGTH - 1))
    },
    [commit, focusIndex],
  )

  /**
   * Fallback for mobile/autofill and cases where change fires without a prior
   * keydown (e.g. SMS one-time-code). Desktop typing is handled in keydown.
   */
  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '')

    if (cleaned.length >= OTP_LENGTH) {
      applyPaste(cleaned, 0)
      return
    }

    if (!cleaned) {
      const next = [...digitsRef.current]
      next[index] = ''
      commit(next)
      return
    }

    // If keydown already wrote this digit, controlled value matches — no-op
    const digit = cleaned.slice(-1)
    if (digitsRef.current[index] === digit && cleaned.length === 1) {
      return
    }

    writeDigitAt(index, digit, true)
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Digit: always write to this box (works with caret after the existing digit)
    if (isDigitKey(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault()
      writeDigitAt(index, e.key, true)
      return
    }

    if (e.key === 'Backspace') {
      e.preventDefault()
      const next = [...digitsRef.current]
      if (next[index]) {
        next[index] = ''
        commit(next)
        focusIndex(index)
      } else if (index > 0) {
        next[index - 1] = ''
        commit(next)
        focusIndex(index - 1)
      }
      return
    }

    if (e.key === 'Delete') {
      e.preventDefault()
      const next = [...digitsRef.current]
      next[index] = ''
      commit(next)
      focusIndex(index)
      return
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      focusIndex(index - 1)
      return
    }

    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault()
      focusIndex(index + 1)
    }
  }

  const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    applyPaste(e.clipboardData.getData('text'), index)
  }

  const selectContents = (el: HTMLInputElement) => {
    el.select()
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    selectContents(e.currentTarget)
  }

  // Click can place the caret after the digit after focus select; re-select
  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    selectContents(e.currentTarget)
  }

  return (
    <div
      className="flex w-full items-center justify-between gap-1.5 sm:gap-2.5"
      role="group"
      aria-label={ariaLabel}
    >
      {digits.map((digit, index) => (
        <input
          key={`${id}-${index}`}
          ref={(el) => {
            inputsRef.current[index] = el
          }}
          id={index === 0 ? id : `${id}-${index}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          maxLength={index === 0 ? OTP_LENGTH : 1}
          disabled={disabled}
          value={digit}
          aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          aria-invalid={invalid}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={handleFocus}
          onClick={handleClick}
          className={cn(
            'aspect-square min-w-0 flex-1 rounded-lg border-2 bg-white text-center text-xl font-semibold text-teal-dark outline-none transition-[border-color,box-shadow] sm:text-2xl',
            'focus:border-teal focus:ring-2 focus:ring-teal/25',
            invalid
              ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
              : 'border-gray-300',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
      ))}
    </div>
  )
}
